import { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { useAppointment } from '../context/appointmentContext';

const PostMessageContext = createContext();
const ReceiveMessageContext = createContext();

export const usePostMessage = () => {
  return useContext(PostMessageContext);
};

export const useReceiveMessage = () => {
  return useContext(ReceiveMessageContext);
};

export const MessageProvider = ({ children }) => {
  const { appointment, messageQueue, updateMessageQueue } = useAppointment();
  const [message, setMessage] = useState(null);

  const postMessage = useCallback(
    (messageContent) => {
      const messageObj = {
        message: {
          content: messageContent,
        },
      };
      appointment?.websocket.postMessage(messageObj);
    },
    [appointment?.websocket],
  );

  useEffect(() => {
    if (!messageQueue?.length) return;
    setMessage(messageQueue[0]?.message?.message?.content);
    updateMessageQueue();
  }, [messageQueue, updateMessageQueue]);

  return (
    <PostMessageContext.Provider value={postMessage}>
      <ReceiveMessageContext.Provider value={message}>{children}</ReceiveMessageContext.Provider>
    </PostMessageContext.Provider>
  );
};

/**
 * LoopbackMessageProvider - development/test helper: every posted message is delivered back to this
 * tab, one per render like the real queue, so screens that sync state through the room socket
 * (updateTestData, updateCalibrationData) work without a call. Used by /start/_showcase test views.
 * `onPost(content)` observes outgoing messages.
 */
export const LoopbackMessageProvider = ({ children, onPost }) => {
  const [queue, setQueue] = useState([]);
  const [message, setMessage] = useState({ type: 'loopbackReady' });

  const postMessage = useCallback(
    (content) => {
      onPost?.(content);
      setQueue((q) => [...q, content]);
    },
    [onPost],
  );

  useEffect(() => {
    if (!queue.length) return;
    setMessage(queue[0]);
    setQueue((q) => q.slice(1));
  }, [queue]);

  return (
    <PostMessageContext.Provider value={postMessage}>
      <ReceiveMessageContext.Provider value={message}>{children}</ReceiveMessageContext.Provider>
    </PostMessageContext.Provider>
  );
};
